import LinksSection from "@/components/wizard/LinksSection";
import { useState } from "react";
import { toast } from "sonner";
import { Globe, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, TextField } from "./Fields";
import ScrapedBox from "./ScrapedBox";
import { AiPrivacyNotice } from "@/components/editor/AiPrivacyNotice";
import ClientPicker, { INTERNAL } from "@/components/sites/ClientPicker";
import { LANGUAGES } from "@/lib/constants";
import { errMsg, useApi } from "@/lib/api";
import { CatalogEditor } from "@/components/catalog/CatalogEditor";
import { ListUpload } from "@/components/wizard/DraftTools";

export const autoName = (company) => (company ? `Sito web — ${company}` : "");

// The company comes from the chosen client card (also one created on the fly); only the internal ADM space asks for it.
function CompanyFrom({ form, set }) {
  if (form.client_id === INTERNAL) return <TextField label="Nome azienda *" value={form.company_name} onChange={(v) => set({ company_name: v, ...(form.name_auto ? { name: autoName(v.trim()) } : {}) })} placeholder="Vitanova Spa" testId="brief-company-input" />;
  if (!form.client_id) return <p className="self-end pb-2 text-xs text-slate-500" data-testid="brief-company-hint">Scegli il cliente: il nome azienda viene preso dalla sua scheda.</p>;
  return (
    <div className="self-end rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="brief-company-derived">
      Nome azienda nel sito: <b className="text-slate-900" data-testid="brief-company-value">{form.company_name || "…"}</b>
      {form.company_legal && form.company_legal !== form.company_name && <span className="block text-slate-500">Ragione sociale: {form.company_legal}</span>}
      <span className="block text-slate-500">Dalla scheda cliente (modificabile in «Clienti»).</span>
    </div>);
}

function CatalogBox({ form, set, api }) {
  const [open, setOpen] = useState((form.catalog || []).length > 0);
  const n = (form.catalog || []).filter((x) => x.name.trim()).length;
  const kind = /ristor|bar|pizz|trattor|food|cucina/i.test(form.sector || "") ? "dish" : /negozi|shop|boutique|store|ecommerce/i.test(form.sector || "") ? "product" : "service";
  return (
    <div className="md:col-span-2 rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="wizard-catalog">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between gap-3 text-left" data-testid="wizard-catalog-toggle">
        <span><span className="block text-sm font-semibold text-slate-900">Servizi e prodotti {n ? `(${n})` : ""}</span>
          <span className="block text-xs text-slate-500">Facoltativo ma consigliato: diventano servizi prenotabili, menu e catalogo reali del cliente (non solo testo). Nessun prezzo o orario viene inventato.</span></span>
        <span className="shrink-0 text-xs text-sky-700">{open ? "Chiudi" : "Apri"}</span>
      </button>
      {open && <><CatalogEditor items={form.catalog || []} onChange={(v) => set({ catalog: v })} api={api} defaultKind={kind} />
        <ListUpload api={api} form={form} set={set} kind={kind} onAdd={(xs) => set({ catalog: [...(form.catalog || []), ...xs] })} /></>}
    </div>);
}

export default function StepCompany({ form, set }) {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const scrape = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/scrape", { url: form.website_url });
      if (data.ok) { set({ scraped: data }); toast.success("Informazioni recuperate"); }
      else toast.error(`${data.error}. Puoi continuare compilando manualmente.`);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  const pick = (c) => {
    const company = c ? (c.name || c.legal_name || "").trim() : "";
    set((f) => ({ company_name: company, company_legal: c?.legal_name || "", ...(f.name_auto || !f.name.trim() ? { name: autoName(company), name_auto: true } : {}) }));
  };
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
      <div className="md:col-span-2 rounded-xl border border-slate-200 bg-white/70 p-4" data-testid="brief-client">
        <Field label="Cliente *">
          <ClientPicker value={form.client_id} onChange={(v) => set({ client_id: v })} onPick={pick} allowInternal testid="brief-client-picker" />
        </Field>
        <p className="mt-2 text-xs text-slate-500">Il sito del cliente viene creato insieme al progetto: dall'editor basta «Pubblica». Moduli e richieste arrivano nel CRM di questo cliente.</p>
      </div>
      <TextField label="Nome progetto *" value={form.name} onChange={(v) => set({ name: v, name_auto: false })} placeholder="Sito web — Vitanova Spa" testId="brief-name-input" />
      <CompanyFrom form={form} set={set} />
      <TextField label="Settore" value={form.sector} onChange={(v) => set({ sector: v })} placeholder="Spa e benessere" testId="brief-sector-input" />
      <Field label="Lingua del sito demo">
        <Select value={form.language} onValueChange={(v) => set({ language: v })}>
          <SelectTrigger className="bg-white" data-testid="brief-language-select"><SelectValue /></SelectTrigger>
          <SelectContent>{LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="URL del sito esistente" hint="Facoltativo, solo come fonte per l'IA (non viene linkato nel sito): analizza fino a 10 pagine (marchio, contenuti, foto, contatti) senza costi AI. Richiede fino a 40 secondi." className="md:col-span-2">
        <div className="flex gap-2">
          <Input value={form.website_url} onChange={(e) => set({ website_url: e.target.value })} placeholder="https://www.esempio.com" className="bg-white" data-testid="brief-url-input" />
          <Button type="button" variant="outline" disabled={!form.website_url || loading} onClick={scrape} data-testid="brief-scrape-btn">
            {loading ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Globe size={15} className="mr-1.5" />}Recupera informazioni
          </Button>
        </div>
      </Field>
      {form.scraped && <div className="md:col-span-2"><ScrapedBox data={form.scraped} onRemove={() => set({ scraped: null, client_images_authorized: false })}
        authorized={form.client_images_authorized} onAuthorize={(v) => set({ client_images_authorized: v })} /></div>}
      <div className="md:col-span-2"><AiPrivacyNotice testid="wizard-ai-privacy-notice" /></div>
      <TextField className="md:col-span-2" multiline label="Descrizione dell'attività" value={form.description} onChange={(v) => set({ description: v })} testId="brief-description-input" />
      <TextField multiline label="Servizi o prodotti principali" value={form.services} onChange={(v) => set({ services: v })} testId="brief-services-input" />
      <CatalogBox form={form} set={set} api={api} />
      <TextField multiline label="Pubblico di riferimento" value={form.audience} onChange={(v) => set({ audience: v })} testId="brief-audience-input" />
      <TextField className="md:col-span-2" multiline label="Obiettivo della demo" value={form.goal} onChange={(v) => set({ goal: v })} testId="brief-goal-input" />
      <LinksSection links={form.links} onChange={(v) => set({ links: v })} />
    </div>
  );
}
